import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { iconChevronDoubleTop } from '@sorare/core/src/atoms/icons/system/iconChevronDoubleTop.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { ButtonWithConfirmDialog } from '@sorare/core/src/components/form/ButtonWithConfirmDialog';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { cart } from '@sorare/core/src/lib/glossary';
import { useAddCollectionToCart } from '@sorare/marketplace/src/hooks/primaryBuy/useAddCollectionToCart';

import { CompleteCollectionModal } from './CompleteCollectionModal/index.tsx';
import { LevelUpModal } from './LevelUpModal/index.tsx';
import type {
  AddCollectionToCartQuery,
  AddCollectionToCartQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = {
  collectionSlug: string;
  rarity: Rarity;
  userSlug: string;
};

type CardCollection = NonNullable<AddCollectionToCartQuery['cardCollection']>;
type UserCardCollection = NonNullable<CardCollection['userCardCollection']>;

const ADD_COLLECTION_TO_CART_QUERY = gql`
  query AddCollectionToCartQuery(
    $collectionSlug: String!
    $userSlug: String!
    $rarity: Rarity
    $skins: [Skin!]
    $withCollectionPredictions: Boolean = true
  ) {
    cardCollection(slug: $collectionSlug) {
      slug
      userCardCollection(forUserSlug: $userSlug) {
        id
        ...useAddCollectionToCart_userCardCollection
        ...LevelUpModal_userCardCollection
        slots(skins: $skins) {
          slug
          slot {
            id
            anyPlayer {
              slug
              anyCardWithLivePrimaryOffer(rarity: $rarity) {
                slug
                ...CompleteCollectionModal_anyCard
                ...LevelUpModal_anyCard
              }
            }
          }
        }
      }
      ...useAddCollectionToCart_cardCollection
      ...LevelUpModal_cardCollection
    }
  }
  ${useAddCollectionToCart.fragments.userCardCollection}
  ${useAddCollectionToCart.fragments.cardCollection}
  ${CompleteCollectionModal.fragments.anyCard}
  ${LevelUpModal.fragments.anyCard}
  ${LevelUpModal.fragments.cardCollection}
  ${LevelUpModal.fragments.userCardCollection}
` as TypedDocumentNode<
  AddCollectionToCartQuery,
  AddCollectionToCartQueryVariables
>;

const FORBIDDEN_RARITIES: Rarity[] = [Rarity.custom_series, Rarity.common];

export const LazyAddCollectionToCartButtons = ({
  collectionSlug,
  rarity,
  userSlug,
}: Props) => {
  const { up: tabletOrAbove } = useScreenSize('tablet');
  const { formatMessage } = useIntlContext();
  const { sport } = useSportContext();

  const skip = FORBIDDEN_RARITIES.includes(rarity);

  const { data, loading } = useQuery(ADD_COLLECTION_TO_CART_QUERY, {
    variables: {
      collectionSlug,
      rarity,
      userSlug,
    },
    skip,
  });
  const userCardCollection = data?.cardCollection?.userCardCollection;
  const cardCollection = data?.cardCollection;

  const {
    addMissingCardsToCart,
    addMissingCardsToNextThresholdToCart,
    unavailableSlots,
    missingCards,
    missingCardsToNextThresholds,
  } = useAddCollectionToCart<UserCardCollection, CardCollection>({
    userCardCollection,
    cardCollection,
    sport,
  });

  if (!data || loading) return null;

  return (
    <Horizontal gap={2} center>
      <Tooltip
        title={
          <BodyS className="text-center">
            {addMissingCardsToNextThresholdToCart.disableReason}
          </BodyS>
        }
        skip={!addMissingCardsToNextThresholdToCart.disabled}
      >
        <ButtonWithConfirmDialog
          disabled={addMissingCardsToNextThresholdToCart.disabled}
          onConfirm={addMissingCardsToNextThresholdToCart.mutate}
          size={tabletOrAbove ? 'medium' : 'small'}
          blur
          fullScreen={false}
          hideCancel
          color="secondary"
          cta={formatMessage(cart.addToCart)}
          startIcon={<FontAwesomeIcon icon={iconChevronDoubleTop} />}
          message={
            <LevelUpModal
              userCardCollection={userCardCollection}
              cardCollection={cardCollection}
              cards={missingCardsToNextThresholds}
            />
          }
          disclaimer={
            <LabelS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Cards already in your cart are not added" />
            </LabelS>
          }
        >
          <FormattedMessage defaultMessage="Level up" />
        </ButtonWithConfirmDialog>
      </Tooltip>
      <Tooltip
        title={
          <BodyS className="text-center">
            {addMissingCardsToCart.disableReason}
          </BodyS>
        }
        skip={!addMissingCardsToCart.disabled}
      >
        <ButtonWithConfirmDialog
          disabled={addMissingCardsToCart.disabled}
          onConfirm={addMissingCardsToCart.mutate}
          size={tabletOrAbove ? 'medium' : 'small'}
          blur
          color="quaternary"
          cta={formatMessage(cart.addToCart)}
          fullScreen={false}
          hideCancel
          startIcon={<FontAwesomeIcon icon={iconCart} />}
          message={<CompleteCollectionModal cards={missingCards} />}
          disclaimer={
            !!unavailableSlots?.length && (
              <LabelS color="var(--c-nd-600)">
                <FormattedMessage
                  defaultMessage="{count, plural, one {# card} other {# cards}} are not available on Instant Buy right now"
                  values={{
                    count: unavailableSlots.length,
                  }}
                />
              </LabelS>
            )
          }
        >
          <FormattedMessage defaultMessage="Complete all" />
        </ButtonWithConfirmDialog>
      </Tooltip>
    </Horizontal>
  );
};
